import type { MeTeam, Season } from "@/lib/types";

/** The season a team implies (#908 D1), or `null` for a legacy team. */
export type SeasonRef = { id: string; name: string } | null;

/** `/api/me` `teams[]` always carries `season` / `status`; the optional
 * type only keeps pre-#908 fixtures compiling. */
export function teamSeason(
  team: Pick<MeTeam, "season"> | null | undefined,
): SeasonRef {
  return team?.season ?? null;
}

export function isArchivedTeam(
  team: { status?: string | null } | null | undefined,
): boolean {
  return (team?.status ?? "active") === "archived";
}

/** How recent a season is: its `starts_on`, else its `created_at` (ISO text
 * compares chronologically). The one rule every season picker uses, the
 * team page's backend order (`routers/tournaments.py`) included; `""` for
 * no season, so a legacy team sorts last. */
export function seasonRecencyKey(
  season: { starts_on?: string | null; created_at?: string | null } | null | undefined,
): string {
  return season ? season.starts_on || season.created_at || "" : "";
}

/** Newest first by `seasonRecencyKey`. */
export function compareSeasonsNewestFirst(a: Season, b: Season): number {
  const aKey = seasonRecencyKey(a);
  const bKey = seasonRecencyKey(b);
  if (aKey === bKey) return 0;
  return aKey < bKey ? 1 : -1;
}

/** The key that ties a team's season teams together (#908 D1): Carry
 * forward links them under one `lineage_key`; a row without one is a
 * lineage of its own. */
export function lineageKeyOf(team: {
  id: string;
  lineage_key?: string | null;
}): string {
  return team.lineage_key || `team:${team.id}`;
}

/** A lineage's members newest season first (by the global season list's
 * order), seasons the list doesn't know next, legacy last, then id. */
export function newestSeasonFirst<T extends { id: string; season: SeasonRef }>(
  items: readonly T[],
  seasons: readonly Season[],
): T[] {
  const order = [...seasons]
    .sort(compareSeasonsNewestFirst)
    .map((season) => season.id);
  const rank = (season: SeasonRef) => {
    if (!season) return Number.MAX_SAFE_INTEGER;
    const index = order.indexOf(season.id);
    return index === -1 ? order.length : index;
  };
  return [...items].sort(
    (a, b) => rank(a.season) - rank(b.season) || a.id.localeCompare(b.id),
  );
}

/** The season team a team's single picker entry opens: its newest current
 * season, else its newest. */
export function lineageRepresentative<
  T extends { id: string; season: SeasonRef; archived: boolean },
>(members: readonly T[], seasons: readonly Season[]): T {
  const sorted = newestSeasonFirst(members, seasons);
  return sorted.find((member) => !member.archived) ?? sorted[0];
}

/** Items grouped by lineage, groups in first-seen order. */
export function groupByLineage<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
): T[][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.values()];
}

/** The season the scope bar treats as implied by `teamId` (#908 D1): the
 * `/api/me` membership entry first, else — for a global admin's
 * non-membership team — its `GET /api/admin/teams` row (the same
 * `admin-teams-switcher` data the TeamSwitcher lists). `null` is a legacy
 * team (free season control); `undefined` means the team is in neither
 * list (yet), e.g. while the admin rows load. */
export function impliedTeamSeason(
  teamId: string | null | undefined,
  meTeams: readonly Pick<MeTeam, "id" | "season">[] | null | undefined,
  adminTeams: readonly { id: string; season_id: string | null }[] | null | undefined,
  seasons: readonly Pick<Season, "id" | "name">[],
): SeasonRef | undefined {
  if (!teamId) return undefined;
  const membership = (meTeams ?? []).find((team) => team.id === teamId);
  if (membership) return teamSeason(membership);
  const row = (adminTeams ?? []).find((team) => team.id === teamId);
  if (!row) return undefined;
  if (!row.season_id) return null;
  return {
    id: row.season_id,
    name: seasons.find((s) => s.id === row.season_id)?.name ?? row.season_id,
  };
}
